import React, { useCallback, useEffect, useRef } from "react";

type ScrollToErrorFormProps = Omit<React.FormHTMLAttributes<HTMLFormElement>, "onInvalidCapture"> & {
  errors: Record<string, string[]>;
  stickyHeaderRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
};

const firstInvalidField = (form: HTMLFormElement | null) => {
  return Array.from(form?.elements || []).find((element) => (
    (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) &&
      !element.validity.valid
  ));
}

// A form that scrolls to the label of the first field with an error, whether
// that's a HTML validation error or a server side error keyed by field id.
const ScrollToErrorForm = ({ errors, stickyHeaderRef, children, ...formProps }: ScrollToErrorFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const scrollToLabelFor = useCallback((id: string) => {
    const labels = Array.from(formRef.current?.querySelectorAll("label") || []);
    const label = labels.find((label) => label.htmlFor === id);
    if (!label) { return; }

    const header = stickyHeaderRef?.current;
    if (header) {
      const stickyTop = parseFloat(window.getComputedStyle(header).top) || 0;
      label.style.scrollMarginTop = stickyTop + header.offsetHeight + "px";
    }

    label.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [stickyHeaderRef]);

  useEffect(() => {
    const firstErrorKey = Object.keys(errors).find((key) => errors[key].length > 0);
    if (firstErrorKey) {
      scrollToLabelFor(firstErrorKey);
    }
  }, [errors, scrollToLabelFor]);

  const onInvalid = (e: React.InvalidEvent<HTMLFormElement>) => {
    const field = e.target as HTMLElement;

    // Only scroll to the first invalid field
    if (field !== firstInvalidField(formRef.current)) { return; }

    // Wait until after the browser's own jump to the invalid input
    requestAnimationFrame(() => scrollToLabelFor(field.id));
  }

  return (
    <form {...formProps} ref={formRef} onInvalidCapture={onInvalid}>
      {children}
    </form>
  );
}

export default ScrollToErrorForm;
